import { Box, InputAdornment, Typography } from '@mui/material';
import { SettingsTitleWithTooltip } from 'content/pages/SettingsPage/helpers';
import { useLoadBalancingConfig } from 'hooks/useLoadBalancingConfig';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';
import type { FC, ChangeEvent } from 'react';
import { generateGroupID } from 'utils/common/loadBalancingHelpers';

import Button from 'components/Button';
import CustomListItem from 'components/DeviceConfigComponents/CustomListItem';
import ErrorText from 'components/ErrorText';
import CustomRadio from 'components/MuiCustom/CustomCheckbox';
import FilledTextField from 'components/MuiCustom/FilledTextField';

import { initialState } from '..';
import type { AddLoadBalancingGroupModalStageProps } from '..';

const CreateLoadBalancingGroup: FC<AddLoadBalancingGroupModalStageProps> = ({
  setState,
  state,
  onClose,
  // eslint-disable-next-line complexity
}) => {
  const { t } = useTranslation();

  const { loadBalancingData, selectedDevices } = state;

  const [enabledForAllDevices, setEnabledForAllDevices] = useState<
    string | undefined
  >(state.selectedGroup ? undefined : 'enable');
  const [loadBalancingGroupId, setLoadBalancingGroupId] = useState<string>(
    loadBalancingData?.loadBalancingGroupId ?? '',
  );
  const [maxTotalCurrent, setMaxTotalCurrent] = useState<number>(
    loadBalancingData?.maxTotalCurrent ?? 32,
  );
  const [maxGridCurrent, setMaxGridCurrent] = useState<number>(
    loadBalancingData?.maxGridCurrent ?? 32,
  );
  const [errorMaxTotalCurrent, setErrorMaxTotalCurrent] =
    useState<boolean>(false);
  const [errorMaxGridCurrent, setErrorMaxGridCurrent] =
    useState<boolean>(false);

  const handleMaxTotalCurrent = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.target.value);

    if (value < 6) {
      setErrorMaxTotalCurrent(true);
    } else {
      setErrorMaxTotalCurrent(false);
    }

    setMaxTotalCurrent(value);
  };

  const handleMaxGridCurrent = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.target.value);

    if (value < 0) {
      setErrorMaxGridCurrent(true);
    } else {
      setErrorMaxGridCurrent(false);
    }

    setMaxGridCurrent(value);
  };

  const hasInvalidLoadBalancingValues =
    errorMaxTotalCurrent ||
    errorMaxGridCurrent ||
    maxTotalCurrent < 0 ||
    maxGridCurrent < 0;

  const handleCreateLoadBalancingGroup = (): void => {
    setState(prev => ({
      ...prev,
      loadBalancingData: {
        loadBalancingEnabled: enabledForAllDevices === 'enable' ? true : false,
        loadBalancingGroupId,
        maxTotalCurrent,
        maxGridCurrent,
      },
      stage: 'devices',
    }));
  };

  const { onSave, loading, error } = useLoadBalancingConfig({
    onSuccess: () => {
      setState(initialState);
      onClose();
    },
  });

  const handleConfigureLoadBalancingGroupData = useCallback(() => {
    onSave({
      serials: selectedDevices,
      ...(enabledForAllDevices !== undefined && {
        loe: enabledForAllDevices === 'enable' ? true : false,
      }),
      ...(maxTotalCurrent !== loadBalancingData?.maxTotalCurrent && {
        amp: maxTotalCurrent,
        sta: maxTotalCurrent,
      }),
      ...(maxGridCurrent !== loadBalancingData?.maxGridCurrent && {
        dyn: maxGridCurrent,
      }),
    });
  }, [
    onSave,
    selectedDevices,
    enabledForAllDevices,
    maxTotalCurrent,
    loadBalancingData?.maxTotalCurrent,
    loadBalancingData?.maxGridCurrent,
    maxGridCurrent,
  ]);

  useEffect(() => {
    if (loadBalancingGroupId.length === 0) {
      const value = generateGroupID();

      setLoadBalancingGroupId(value);
    }
  }, [loadBalancingGroupId.length]);

  return (
    <Box
      sx={{
        overflow: 'auto',
        width: '100%',
      }}
    >
      <form>
        <Box mb={3}>
          <Typography
            variant="h2"
            textAlign="center"
            mb={1}
            data-testid="add-load-balancing-modal-title"
          >
            {state.configureGroup
              ? t(
                  'common:pages.load_balancing.add_load_balancing_group.group_config',
                )
              : t('common:pages.load_balancing.add_load_balancing_group.title')}
          </Typography>
        </Box>
        <Box mb={4} mt={4}>
          <Typography mb={1} ml={1.5} fontWeight={600} display="flex">
            {t('common:pages.load_balancing.page_title')}
          </Typography>
          {['enable', 'disable'].map((option, index, { length }) => (
            <CustomListItem
              key={option}
              leftText={t(
                `common:pages.load_balancing.add_load_balancing_group.${option}_all_devices`,
              )}
              onClick={() => {
                setEnabledForAllDevices(
                  enabledForAllDevices === option ? undefined : option,
                );
              }}
              leftComponent={
                <CustomRadio
                  checked={enabledForAllDevices === option}
                  value={enabledForAllDevices}
                  sx={{ p: 0 }}
                />
              }
              isFirstItem={index === 0}
              isLastItem={index === length - 1}
            />
          ))}
        </Box>
        <Box mb={4} mt={4}>
          <CustomListItem
            leftText={t(
              'common:pages.settings.subpages.load_balancing.group_id',
            )}
            isSingleItem
            rightText={loadBalancingGroupId}
          />
        </Box>
        <Box mb={4} mt={4}>
          <SettingsTitleWithTooltip
            title={t(
              'common:pages.device_setup.load_balancing_setup.dynamic_load_balancing',
            )}
            tooltip={t(
              'common:pages.device_setup.load_balancing_setup.dynamic_load_balancing_desc',
            )}
          />
          <FilledTextField
            label={t(
              'common:pages.settings.subpages.load_balancing.maximum_grid_current',
            )}
            name="maxGridCurrent"
            type="number"
            value={maxGridCurrent}
            onChange={handleMaxGridCurrent}
            error={errorMaxGridCurrent}
            helperText={
              errorMaxGridCurrent
                ? t(
                    'common:pages.device_setup.load_balancing_setup.min_value_error',
                    {
                      min: 0,
                    },
                  )
                : ''
            }
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {t('common:ampere_long').toLocaleLowerCase()}
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box mb={4} mt={4}>
          <SettingsTitleWithTooltip
            title={t(
              'common:pages.device_setup.load_balancing_setup.static_load_balancing',
            )}
            tooltip={t(
              'common:pages.device_setup.load_balancing_setup.static_load_balancing_desc',
            )}
          />
          <FilledTextField
            label={t(
              'common:pages.settings.subpages.load_balancing.maximum_total_current',
            )}
            name="maxTotalCurrent"
            type="number"
            value={maxTotalCurrent}
            onChange={handleMaxTotalCurrent}
            error={errorMaxTotalCurrent}
            helperText={
              errorMaxTotalCurrent
                ? t(
                    'common:pages.device_setup.load_balancing_setup.min_value_error',
                    {
                      min: 6,
                    },
                  )
                : ''
            }
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {t('common:ampere_long').toLocaleLowerCase()}
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <ErrorText error={error} mt={2} />
        <Box mb={2} mt={4}>
          <Button
            fullWidth
            variant="flat"
            color="primary"
            type="submit"
            disabled={hasInvalidLoadBalancingValues || loading}
            onClick={
              state.configureGroup
                ? handleConfigureLoadBalancingGroupData
                : handleCreateLoadBalancingGroup
            }
            size="large"
            data-testid="submit"
          >
            {state.configureGroup
              ? t('common:save')
              : t(
                  'common:pages.load_balancing.add_load_balancing_group.select_devices',
                )}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default CreateLoadBalancingGroup;
